/**
 * QAnalysisBoard Component (v1.0)
 * Displays AI analysis results in a professional dashboard format
 *
 * States:
 * - Empty: Placeholder with icon and instruction text
 * - Populated: Shows analysis data (performance, signals, or custom)
 *
 * Matches platform dark theme and institutional styling
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';

// Color constants matching platform theme
const COLORS = {
  background: '#0A0A0F',
  cardBg: 'rgba(255, 255, 255, 0.02)',
  cardBgHover: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.06)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  headerText: '#F5F5F5',
  bodyText: '#E4E4E7',
  mutedText: '#71717A',
  dimText: '#52525B',
  green: '#27B56D',
  greenBg: 'rgba(39, 181, 109, 0.1)',
  red: '#EF4444',
  redBg: 'rgba(239, 68, 68, 0.1)',
  yellow: '#F59E0B',
  yellowBg: 'rgba(245, 158, 11, 0.1)',
  blue: '#3B82F6',
  blueBg: 'rgba(59, 130, 246, 0.1)'
};

/**
 * QAnalysisBoard Component
 */
const QAnalysisBoard = ({
  analysisData = null,
  isLive = false,
  lastUpdated = null
}) => {
  const isEmpty = !analysisData;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: COLORS.background,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <BoardHeader isLive={isLive && !isEmpty} />

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '2px'
          }
        }}
      >
        {isEmpty ? (
          <EmptyState />
        ) : (
          <PopulatedState data={analysisData} />
        )}
      </Box>

      {/* Footer */}
      <BoardFooter lastUpdated={lastUpdated} isEmpty={isEmpty} />
    </Box>
  );
};

/**
 * Board Header
 */
const BoardHeader = ({ isLive }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderBottom: `1px solid ${COLORS.border}`,
      backgroundColor: COLORS.background
    }}
  >
    {/* Left: Q Icon + Title */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)'
        }}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.875rem',
            lineHeight: 1
          }}
        >
          Q
        </Typography>
      </Box>
      <Typography
        sx={{
          color: COLORS.headerText,
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '-0.01em'
        }}
      >
        AI Analysis Board
      </Typography>
    </Box>

    {/* Right: Live Indicator */}
    {isLive && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: COLORS.green,
            boxShadow: `0 0 8px ${COLORS.green}`,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 }
            }
          }}
        />
        <Typography
          sx={{
            color: COLORS.green,
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        >
          Live
        </Typography>
      </Box>
    )}
  </Box>
);

/**
 * Empty State
 */
const EmptyState = () => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 2,
      padding: 4
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '16px',
        backgroundColor: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <SearchIcon sx={{ fontSize: 28, color: COLORS.dimText }} />
    </Box>
    <Box>
      <Typography
        sx={{
          color: COLORS.mutedText,
          fontSize: '1rem',
          fontWeight: 500,
          mb: 0.5
        }}
      >
        Ask Q to analyze your trading
      </Typography>
      <Typography
        sx={{
          color: COLORS.dimText,
          fontSize: '0.875rem'
        }}
      >
        or request market signals
      </Typography>
    </Box>
  </Box>
);

/**
 * Populated State - Routes to correct display type
 */
const PopulatedState = ({ data }) => {
  switch (data.type) {
    case 'performance':
      return <PerformanceView data={data} />;
    case 'signals':
      return <SignalsView data={data} />;
    case 'trades':
      return <TradesView data={data} />;
    default:
      return <CustomView data={data} />;
  }
};

/**
 * Performance Analysis View
 */
const PerformanceView = ({ data }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {/* Title Section */}
    <Box>
      <Typography
        sx={{
          color: COLORS.headerText,
          fontSize: '1.125rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5
        }}
      >
        📊 {data.title || 'Your Trading Performance'}
      </Typography>
      {data.subtitle && (
        <Typography sx={{ color: COLORS.mutedText, fontSize: '0.875rem' }}>
          {data.subtitle}
        </Typography>
      )}
    </Box>

    {/* Stats Cards */}
    {data.stats && data.stats.length > 0 && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2
        }}
      >
        {data.stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </Box>
    )}

    {/* Strengths */}
    {data.strengths && data.strengths.length > 0 && (
      <InsightSection
        icon="✅"
        title="Strengths"
        items={data.strengths}
        color={COLORS.green}
      />
    )}

    {/* Areas to Improve */}
    {data.improvements && data.improvements.length > 0 && (
      <InsightSection
        icon="⚠️"
        title="Areas to Improve"
        items={data.improvements}
        color={COLORS.yellow}
      />
    )}

    {/* Potential Impact */}
    {data.potential && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '12px 16px',
          backgroundColor: COLORS.greenBg,
          borderRadius: '8px',
          border: `1px solid rgba(39, 181, 109, 0.2)`
        }}
      >
        <Typography sx={{ fontSize: '1rem' }}>💡</Typography>
        <Typography sx={{ color: COLORS.green, fontSize: '0.9375rem', fontWeight: 500 }}>
          Potential Impact: {data.potential}
        </Typography>
      </Box>
    )}
  </Box>
);

/**
 * Stat Card Component
 */
const StatCard = ({ label, value, color = 'default', icon }) => {
  const getColor = () => {
    switch (color) {
      case 'green': return COLORS.green;
      case 'red': return COLORS.red;
      case 'yellow': return COLORS.yellow;
      default: return COLORS.headerText;
    }
  };

  return (
    <Box
      sx={{
        padding: '16px',
        backgroundColor: COLORS.cardBg,
        borderRadius: '10px',
        border: `1px solid ${COLORS.border}`,
        textAlign: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: COLORS.cardBgHover,
          borderColor: COLORS.borderLight
        }
      }}
    >
      <Typography
        sx={{
          color: COLORS.mutedText,
          fontSize: '0.75rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mb: 0.75
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: getColor(),
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: 1.2
        }}
      >
        {value}
      </Typography>
      {icon && (
        <Typography sx={{ fontSize: '1rem', mt: 0.5 }}>{icon}</Typography>
      )}
    </Box>
  );
};

/**
 * Insight Section (Strengths / Improvements)
 */
const InsightSection = ({ icon, title, items, color }) => (
  <Box>
    <Typography
      sx={{
        color: COLORS.headerText,
        fontSize: '0.9375rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mb: 1.5
      }}
    >
      {icon} {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            paddingLeft: 1
          }}
        >
          <Typography sx={{ color, fontSize: '0.875rem' }}>•</Typography>
          <Typography sx={{ color: COLORS.bodyText, fontSize: '0.875rem', lineHeight: 1.5 }}>
            {typeof item === 'string' ? item : item.text}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/**
 * Signals View
 */
const SignalsView = ({ data }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {/* Title */}
    <Typography
      sx={{
        color: COLORS.headerText,
        fontSize: '1.125rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      ⚡ {data.title || 'Market Signals'}
    </Typography>

    {/* Signal Cards */}
    {data.signals && data.signals.map((signal, index) => (
      <SignalCard key={index} {...signal} />
    ))}
  </Box>
);

/**
 * Signal Card Component
 */
const SignalCard = ({ pair, direction, entry, score }) => {
  const getDirectionConfig = () => {
    switch (direction?.toUpperCase()) {
      case 'BUY':
        return { color: COLORS.green, bg: COLORS.greenBg, icon: <TrendingUpIcon /> };
      case 'SELL':
        return { color: COLORS.red, bg: COLORS.redBg, icon: <TrendingDownIcon /> };
      default:
        return { color: COLORS.mutedText, bg: COLORS.cardBg, icon: <RemoveIcon /> };
    }
  };

  const config = getDirectionConfig();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        backgroundColor: COLORS.cardBg,
        borderRadius: '10px',
        border: `1px solid ${COLORS.border}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: COLORS.cardBgHover,
          borderColor: config.color
        }
      }}
    >
      {/* Left: Icon + Pair Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: config.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: config.color,
            '& svg': { fontSize: '1.125rem' }
          }}
        >
          {config.icon}
        </Box>
        <Box>
          <Typography
            sx={{
              color: COLORS.headerText,
              fontSize: '0.9375rem',
              fontWeight: 600
            }}
          >
            {pair}
          </Typography>
          <Typography sx={{ color: COLORS.dimText, fontSize: '0.75rem' }}>
            Entry: {entry || '—'}
          </Typography>
        </Box>
      </Box>

      {/* Right: Direction + Score */}
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            color: config.color,
            fontSize: '0.8125rem',
            fontWeight: 600,
            textTransform: 'uppercase'
          }}
        >
          {direction || 'WAIT'}
        </Typography>
        <Typography sx={{ color: COLORS.dimText, fontSize: '0.75rem' }}>
          Q Score: {score || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Trades History View (E4)
 */
const TradesView = ({ data }) => {
  const trades = data.trades || [];
  const summary = data.summary || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Title */}
      <Box>
        <Typography
          sx={{
            color: COLORS.headerText,
            fontSize: '1.125rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5
          }}
        >
          📈 {data.title || 'Your Trade History'}
        </Typography>
        {data.subtitle && (
          <Typography sx={{ color: COLORS.mutedText, fontSize: '0.875rem' }}>
            {data.subtitle}
          </Typography>
        )}
      </Box>

      {/* Summary Stats */}
      {summary && Object.keys(summary).length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 2
          }}
        >
          <StatCard
            label="Total Trades"
            value={summary.totalTrades?.toString() || '0'}
            color="default"
          />
          <StatCard
            label="Win Rate"
            value={summary.winRate ? `${summary.winRate}%` : '—'}
            color={summary.winRate >= 50 ? 'green' : 'red'}
          />
          <StatCard
            label="Total P&L"
            value={summary.totalPnL ? `$${summary.totalPnL.toFixed(2)}` : '—'}
            color={summary.totalPnL >= 0 ? 'green' : 'red'}
          />
          <StatCard
            label="Avg Trade"
            value={summary.avgPnL ? `$${summary.avgPnL.toFixed(2)}` : '—'}
            color={summary.avgPnL >= 0 ? 'green' : 'red'}
          />
        </Box>
      )}

      {/* Trades List */}
      {trades.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              color: COLORS.mutedText,
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 0.5
            }}
          >
            Recent Trades ({trades.length})
          </Typography>
          {trades.slice(0, 10).map((trade, index) => (
            <TradeRow key={trade.id || index} trade={trade} />
          ))}
          {trades.length > 10 && (
            <Typography
              sx={{
                color: COLORS.dimText,
                fontSize: '0.8125rem',
                textAlign: 'center',
                mt: 1
              }}
            >
              + {trades.length - 10} more trades
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            padding: 3,
            backgroundColor: COLORS.cardBg,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: COLORS.mutedText, fontSize: '0.9375rem' }}>
            No trades synced yet. Connect QBridge to import your MT5 history.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Trade Row Component
 */
const TradeRow = ({ trade }) => {
  const isProfit = (trade.profit || trade.pnl || 0) >= 0;
  const pnl = trade.profit || trade.pnl || 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        backgroundColor: COLORS.cardBg,
        borderRadius: '8px',
        border: `1px solid ${COLORS.border}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: COLORS.cardBgHover,
          borderColor: isProfit ? COLORS.green : COLORS.red
        }
      }}
    >
      {/* Left: Symbol + Type */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            backgroundColor: trade.type?.toLowerCase() === 'buy' ? COLORS.greenBg : COLORS.redBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {trade.type?.toLowerCase() === 'buy' ? (
            <TrendingUpIcon sx={{ fontSize: '1rem', color: COLORS.green }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: '1rem', color: COLORS.red }} />
          )}
        </Box>
        <Box>
          <Typography
            sx={{
              color: COLORS.headerText,
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            {trade.symbol || '—'}
          </Typography>
          <Typography sx={{ color: COLORS.dimText, fontSize: '0.6875rem' }}>
            {trade.volume || trade.lots || '—'} lots • {trade.type || '—'}
          </Typography>
        </Box>
      </Box>

      {/* Center: Date */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ color: COLORS.mutedText, fontSize: '0.75rem' }}>
          {trade.closeTime
            ? new Date(trade.closeTime).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })
            : '—'}
        </Typography>
      </Box>

      {/* Right: P&L */}
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            color: isProfit ? COLORS.green : COLORS.red,
            fontSize: '0.9375rem',
            fontWeight: 600
          }}
        >
          {isProfit ? '+' : ''}{pnl.toFixed(2)}
        </Typography>
        <Typography sx={{ color: COLORS.dimText, fontSize: '0.6875rem' }}>
          {trade.pips ? `${trade.pips} pips` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Custom/Generic View
 */
const CustomView = ({ data }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {data.title && (
      <Typography
        sx={{
          color: COLORS.headerText,
          fontSize: '1.125rem',
          fontWeight: 600
        }}
      >
        {data.title}
      </Typography>
    )}
    {data.content && (
      <Typography
        sx={{
          color: COLORS.bodyText,
          fontSize: '0.9375rem',
          lineHeight: 1.7
        }}
      >
        {data.content}
      </Typography>
    )}
  </Box>
);

/**
 * Board Footer
 */
const BoardFooter = ({ lastUpdated, isEmpty }) => (
  <Box
    sx={{
      padding: '10px 16px',
      borderTop: `1px solid ${COLORS.border}`,
      backgroundColor: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5
    }}
  >
    <Typography sx={{ color: COLORS.dimText, fontSize: '0.6875rem' }}>
      Q reads pre-computed signals
    </Typography>
    {!isEmpty && lastUpdated && (
      <>
        <Typography sx={{ color: COLORS.dimText, fontSize: '0.6875rem' }}>•</Typography>
        <Typography sx={{ color: COLORS.dimText, fontSize: '0.6875rem' }}>
          {lastUpdated}
        </Typography>
      </>
    )}
  </Box>
);

// PropTypes
QAnalysisBoard.propTypes = {
  analysisData: PropTypes.shape({
    type: PropTypes.oneOf(['performance', 'signals', 'trades', 'custom']),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    stats: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      color: PropTypes.oneOf(['default', 'green', 'red', 'yellow']),
      icon: PropTypes.string
    })),
    strengths: PropTypes.array,
    improvements: PropTypes.array,
    potential: PropTypes.string,
    signals: PropTypes.arrayOf(PropTypes.shape({
      pair: PropTypes.string,
      direction: PropTypes.string,
      entry: PropTypes.string,
      score: PropTypes.number
    })),
    trades: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      symbol: PropTypes.string,
      type: PropTypes.string,
      volume: PropTypes.number,
      profit: PropTypes.number,
      pnl: PropTypes.number,
      closeTime: PropTypes.string,
      pips: PropTypes.number
    })),
    summary: PropTypes.shape({
      totalTrades: PropTypes.number,
      winRate: PropTypes.number,
      totalPnL: PropTypes.number,
      avgPnL: PropTypes.number
    }),
    content: PropTypes.string
  }),
  isLive: PropTypes.bool,
  lastUpdated: PropTypes.string
};

export default QAnalysisBoard;
